import { useState, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../../../../../context/SnackbarContextProvider';
import {
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  OnboardingUserFormValues,
} from '../../../types';
import { canDeleteUsers } from './userReferenceUtils';
import TableHeader from './components/tableHeader';
import UsersTable from './components/UsersTable';
import AddEditUserDialog from './AddEditUserDialog';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

const Users = ({ selectedFieldPath }: Props) => {
  const { i18n } = useTranslation();
  const isHebrew = i18n.language === 'heb';
  const { showSnackbar } = useSnackbar();

  const { watch, getValues, setValue } =
    useFormContext<NetworkDetailsFormValues>();

  const nodeUsers =
    (watch(
      `${selectedFieldPath}.users` as any,
    ) as OnboardingUserFormValues[]) || [];
  const departments = watch(`${selectedFieldPath}.departments`);
  const roles = watch(`${selectedFieldPath}.roles`);
  const allNetworks = watch('network');

  const clients = useMemo(
    () =>
      allNetworks.map((n) => ({
        id: n.id,
        name: n.name || 'ארגון ללא שם',
        departments: n.departments,
        roles: n.roles,
        branches: n.branches.map((b) => ({
          id: b.id,
          name: b.name || 'סניף ללא שם',
          departments: b.departments,
          roles: b.roles,
          subBranches: b.subBranches.map((sb) => ({
            id: sb.id,
            name: sb.name || 'תת-סניף ללא שם',
            departments: sb.departments,
            roles: sb.roles,
          })),
        })),
      })),
    [allNetworks],
  );
  const initialLocation = useMemo(() => {
    const [, networkIndex, , branchIndex, , subBranchIndex] =
      selectedFieldPath.split('.');
    const networkNode = allNetworks[Number(networkIndex)] ?? allNetworks[0];
    const branchNode =
      branchIndex !== undefined
        ? networkNode?.branches[Number(branchIndex)]
        : undefined;
    const subBranchNode =
      subBranchIndex !== undefined
        ? branchNode?.subBranches[Number(subBranchIndex)]
        : undefined;

    return {
      clientId: networkNode?.id ?? '',
      branchId: branchNode?.id,
      subBranchId: subBranchNode?.id,
    };
  }, [allNetworks, selectedFieldPath]);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedUserIndex, setSelectedUserIndex] = useState<number | null>(
    null,
  );
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  const selectedUser =
    selectedUserIndex !== null ? (nodeUsers[selectedUserIndex] ?? null) : null;

  const filteredUsers = useMemo(() => {
    if (!searchText) return nodeUsers;
    const q = searchText.toLowerCase();
    return nodeUsers.filter(
      (u) =>
        u.firstName.toLowerCase().includes(q) ||
        u.lastName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [nodeUsers, searchText]);

  // Add/update/remove a single user entry at a given RHF path
  const updateNodeUsers = (
    path: string,
    userId: string,
    user: OnboardingUserFormValues | null,
  ) => {
    const current =
      ((getValues as any)(path) as OnboardingUserFormValues[]) || [];
    const idx = current.findIndex((u) => u.id === userId);
    if (user !== null) {
      if (idx >= 0) {
        const next = [...current];
        next[idx] = user;
        (setValue as any)(path, next, { shouldDirty: true });
      } else {
        (setValue as any)(path, [...current, user], { shouldDirty: true });
      }
    } else if (idx >= 0) {
      (setValue as any)(
        path,
        current.filter((u) => u.id !== userId),
        { shouldDirty: true },
      );
    }
  };

  // Sync a user across ALL network nodes based on their clientIds / branchIds / subBranchIds
  const syncUserAcrossNetwork = (user: OnboardingUserFormValues) => {
    const network = getValues('network');
    network.forEach((net, ni) => {
      updateNodeUsers(
        `network.${ni}.users`,
        user.id,
        user.clientIds.includes(net.id) ? user : null,
      );
      net.branches.forEach((branch, bi) => {
        updateNodeUsers(
          `network.${ni}.branches.${bi}.users`,
          user.id,
          user.branchIds.includes(branch.id) ? user : null,
        );
        branch.subBranches.forEach((sb, si) => {
          updateNodeUsers(
            `network.${ni}.branches.${bi}.subBranches.${si}.users`,
            user.id,
            user.subBranchIds?.includes(sb.id) ? user : null,
          );
        });
      });
    });
  };

  // Remove users by IDs from every node in the network tree
  const removeUsersFromNetwork = (userIds: string[]) => {
    if (!userIds.length) return;
    const idSet = new Set(userIds);
    const network = getValues('network');
    network.forEach((net, ni) => {
      const nu =
        ((getValues as any)(
          `network.${ni}.users`,
        ) as OnboardingUserFormValues[]) || [];
      if (nu.some((u) => idSet.has(u.id))) {
        (setValue as any)(
          `network.${ni}.users`,
          nu.filter((u) => !idSet.has(u.id)),
          { shouldDirty: true },
        );
      }
      net.branches.forEach((branch, bi) => {
        const bu =
          ((getValues as any)(
            `network.${ni}.branches.${bi}.users`,
          ) as OnboardingUserFormValues[]) || [];
        if (bu.some((u) => idSet.has(u.id))) {
          (setValue as any)(
            `network.${ni}.branches.${bi}.users`,
            bu.filter((u) => !idSet.has(u.id)),
            { shouldDirty: true },
          );
        }
        branch.subBranches.forEach((_, si) => {
          const su =
            ((getValues as any)(
              `network.${ni}.branches.${bi}.subBranches.${si}.users`,
            ) as OnboardingUserFormValues[]) || [];
          if (su.some((u) => idSet.has(u.id))) {
            (setValue as any)(
              `network.${ni}.branches.${bi}.subBranches.${si}.users`,
              su.filter((u) => !idSet.has(u.id)),
              { shouldDirty: true },
            );
          }
        });
      });
    });
  };

  const handleSave = (data: Omit<OnboardingUserFormValues, 'id'>) => {
    const now = new Date().toISOString();
    const existingUser =
      selectedUserIndex !== null ? nodeUsers[selectedUserIndex] : null;
    const userId = existingUser ? existingUser.id : crypto.randomUUID();
    syncUserAcrossNetwork({
      ...data,
      id: userId,
      createdAt: existingUser?.createdAt ?? now,
      updatedAt: now,
      isAppUser: existingUser?.isAppUser ?? false,
    });
    setShowDialog(false);
    setSelectedUserIndex(null);
    setSelectedKeys([]);
  };

  const handleImportUsers = (
    importedUsers: Omit<OnboardingUserFormValues, 'id'>[],
  ) => {
    importedUsers.forEach((user) => {
      const now = new Date().toISOString();
      syncUserAcrossNetwork({
        ...user,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
        isAppUser: false,
      });
    });
  };

  const handleDelete = () => {
    const usersToDelete = nodeUsers.filter((u) => selectedKeys.includes(u.id));
    const result = canDeleteUsers(allNetworks, usersToDelete);

    if (!result.allowed) {
      showSnackbar({
        message: result.message,
        type: 'error',
        duration: 5000,
      });
      return;
    }

    removeUsersFromNetwork(selectedKeys);
    setSelectedKeys([]);
  };

  return (
    <>
      <div
        className={`flex-1 min-h-0 overflow-x-hidden flex flex-col ${isHebrew ? 'rounded-tl-[32px]' : 'rounded-tr-[32px]'}`}
      >
        <TableHeader
          usersCount={nodeUsers.length}
          searchText={searchText}
          onSearchChange={setSearchText}
          onAdd={() => {
            setSelectedUserIndex(null);
            setShowDialog(true);
          }}
          onEdit={() => {
            const idx = nodeUsers.findIndex((u) => selectedKeys.includes(u.id));
            if (idx !== -1) {
              setSelectedUserIndex(idx);
              setShowDialog(true);
            }
          }}
          onDelete={handleDelete}
          isEditDisabled={selectedKeys.length !== 1}
          isDeleteDisabled={selectedKeys.length === 0}
          departments={departments}
          roles={roles}
          clients={clients}
          users={nodeUsers}
          onImportUsers={handleImportUsers}
        />
        <UsersTable
          users={filteredUsers}
          roles={roles}
          clients={clients}
          departments={departments}
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
        />
      </div>

      <AddEditUserDialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          setSelectedUserIndex(null);
        }}
        onSave={handleSave}
        selectedUser={selectedUser}
        departments={departments}
        roles={roles}
        clients={clients}
        existingUsers={nodeUsers}
        initialLocation={initialLocation}
      />
    </>
  );
};

export default Users;
